import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PaneResizeHandle } from '@/components/ui/pane-resize-handle'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type {
  FormFolderContract,
  FormUiContract,
} from '@/contracts/questionnaire'
import { useResizablePane } from '@/hooks/use-resizable-pane'
import { formFolderPaneDefaults } from '@/lib/resizable-panes'
import { cn } from '@/lib/utils'
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Edit3, FolderOpen, GripVertical, Plus, Trash2 } from 'lucide-react'
import { type CSSProperties, useMemo, useState } from 'react'
import type { FormFolderDraft, PublicationStatusFilter } from './types'

function normalizeFolderName(value: string) {
  return value.trim().replace(/\s+/g, ' ')
}

function uniqueFormIds(formIds: string[]) {
  return Array.from(new Set(formIds))
}

type FormFolderSidebarProps = {
  forms: FormUiContract[]
  folders: FormFolderContract[]
  activeFolderId: string
  publicationStatus: PublicationStatusFilter
  canManageForms: boolean
  onSelectFolder: (folderId: string) => void
  onPublicationStatusChange: (status: PublicationStatusFilter) => void
  onCreateFolder: (draft: FormFolderDraft) => void
  onUpdateFolder: (folderId: string, draft: FormFolderDraft) => void
  onRemoveFolder: (folderId: string) => void
  onReorderFolders: (activeId: string, overId: string) => void
}

export function FormFolderSidebar({
  forms,
  folders,
  activeFolderId,
  publicationStatus,
  canManageForms,
  onSelectFolder,
  onPublicationStatusChange,
  onCreateFolder,
  onUpdateFolder,
  onRemoveFolder,
  onReorderFolders,
}: FormFolderSidebarProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingFolder, setEditingFolder] = useState<FormFolderContract | null>(null)
  const [folderName, setFolderName] = useState('')
  const [folderFormIds, setFolderFormIds] = useState<string[]>([])
  const [folderError, setFolderError] = useState<string | null>(null)
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false)
  const {
    width: sidebarWidth,
    collapsed: sidebarCollapsed,
    startResize: startSidebarResize,
    toggleCollapsed: toggleSidebarCollapsed,
  } = useResizablePane({
    initialWidth: 288,
    config: formFolderPaneDefaults.nav,
  })
  const sidebarCssWidth = sidebarCollapsed ? formFolderPaneDefaults.nav.collapsed : sidebarWidth

  const folderNameKeys = useMemo(
    () =>
      new Map(
        folders.map((folder) => [folder.id, normalizeFolderName(folder.name).toLocaleLowerCase()]),
      ),
    [folders],
  )

  function openCreateDialog() {
    if (!canManageForms) return
    setEditingFolder(null)
    setFolderName('')
    setFolderFormIds([])
    setFolderError(null)
    setDialogOpen(true)
  }

  function openEditDialog(folder: FormFolderContract) {
    if (!canManageForms) return
    setEditingFolder(folder)
    setFolderName(folder.name)
    setFolderFormIds(uniqueFormIds(folder.formIds))
    setFolderError(null)
    setDialogOpen(true)
  }

  function handleDragEnd(event: DragEndEvent) {
    if (!canManageForms) return
    const { active, over } = event
    if (!over || active.id === over.id) return
    onReorderFolders(String(active.id), String(over.id))
  }

  function toggleFolderForm(formId: string) {
    if (!canManageForms) return
    setFolderError(null)
    setFolderFormIds((current) =>
      current.includes(formId)
        ? current.filter((id) => id !== formId)
        : uniqueFormIds([...current, formId]),
    )
  }

  function saveFolder() {
    if (!canManageForms) return
    const normalizedName = normalizeFolderName(folderName)
    if (!normalizedName) {
      setFolderError('請輸入資料夾名稱。')
      return
    }

    const normalizedKey = normalizedName.toLocaleLowerCase()
    const duplicated = Array.from(folderNameKeys.entries()).some(
      ([folderId, key]) => folderId !== editingFolder?.id && key === normalizedKey,
    )
    if (duplicated) {
      setFolderError('已有相同名稱的資料夾，請使用不同名稱。')
      return
    }

    const draft = { name: normalizedName, formIds: uniqueFormIds(folderFormIds) }
    if (editingFolder) onUpdateFolder(editingFolder.id, draft)
    else onCreateFolder(draft)

    setDialogOpen(false)
    setFolderError(null)
  }

  function removeFolder() {
    if (!editingFolder || !canManageForms) return
    onRemoveFolder(editingFolder.id)
    setRemoveConfirmOpen(false)
    setDialogOpen(false)
  }

  return (
    <aside
      data-testid="form-folder-sidebar"
      className="relative hidden h-full min-h-0 shrink-0 border-r border-slate-200 bg-white transition-[width] duration-200 ease-out lg:flex lg:flex-col"
      style={
        {
          width: sidebarCssWidth,
          '--form-folder-sidebar-width': `${sidebarCssWidth}px`,
        } as CSSProperties
      }
    >
      <div
        className={cn(
          'flex h-full min-h-0 flex-col transition-opacity duration-150',
          sidebarCollapsed && 'pointer-events-none opacity-0',
        )}
        aria-hidden={sidebarCollapsed}
      >
        <div className="shrink-0 px-4 py-4">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-sm font-semibold text-slate-950">問卷管理</div>
              <div className="mt-1 text-xs text-slate-500">資料夾與搜尋篩選</div>
            </div>
            {canManageForms && (
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                onClick={openCreateDialog}
                aria-label="新增資料夾"
                title="新增資料夾"
              >
                <Plus className="size-4" />
              </Button>
            )}
          </div>

          <div className="mt-4 space-y-3">
            <div>
              <Label className="text-xs text-slate-500">草稿 / 公開狀態</Label>
              <Select
                value={publicationStatus}
                onValueChange={(value) =>
                  onPublicationStatusChange(value as PublicationStatusFilter)
                }
              >
                <SelectTrigger className="mt-1 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部</SelectItem>
                  <SelectItem value="draft">草稿</SelectItem>
                  <SelectItem value="published">公開</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="shrink-0 border-t border-slate-100 px-4 py-3">
          <div className="flex items-center justify-between text-xs font-medium text-slate-500">
            <span>資料夾</span>
            <span>可多重歸類</span>
          </div>
        </div>

        <ScrollArea
          className="min-h-0 flex-1 border-t border-slate-100"
          viewportClassName="px-3 py-3"
          data-testid="folder-scroll-area"
        >
          <button
            className={cn(
              'mb-1 flex h-10 w-full items-center gap-2 rounded-md px-3 text-left text-sm font-medium transition-colors',
              activeFolderId === 'all'
                ? 'bg-indigo-50 text-indigo-700'
                : 'text-slate-600 hover:bg-slate-50',
            )}
            type="button"
            onClick={() => onSelectFolder('all')}
          >
            <FolderOpen className="size-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate">所有問卷</span>
            <span
              className={cn(
                'text-xs',
                activeFolderId === 'all' ? 'text-indigo-700' : 'text-slate-500',
              )}
            >
              {forms.length}
            </span>
          </button>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={folders.map((folder) => folder.id)}
              strategy={verticalListSortingStrategy}
            >
              {folders.map((folder) => (
                <SortableFolderItem
                  key={folder.id}
                  folder={folder}
                  active={activeFolderId === folder.id}
                  canManageForms={canManageForms}
                  onSelect={() => onSelectFolder(folder.id)}
                  onEdit={() => openEditDialog(folder)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </ScrollArea>
      </div>

      <PaneResizeHandle
        edge="right"
        collapsed={sidebarCollapsed}
        resizeLabel="拖曳調整問卷資料夾左欄寬度"
        collapseLabel="收合問卷資料夾左欄"
        expandLabel="展開問卷資料夾左欄"
        onResizeStart={startSidebarResize}
        onToggle={toggleSidebarCollapsed}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingFolder ? '編輯資料夾' : '新增資料夾'}</DialogTitle>
            <DialogDescription>
              問卷可同時歸類在不同資料夾；此批次只儲存在前端 mock state。
            </DialogDescription>
          </DialogHeader>

          <div className="mt-5 space-y-5">
            <div>
              <Label htmlFor="folder-name">資料夾名稱</Label>
              <Input
                id="folder-name"
                value={folderName}
                onChange={(event) => {
                  setFolderName(event.target.value)
                  setFolderError(null)
                }}
                placeholder="例如：客戶建檔"
                className="mt-2"
              />
              {folderError && <p className="mt-2 text-sm text-rose-600">{folderError}</p>}
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label>歸類問卷</Label>
                <Badge variant="secondary">{folderFormIds.length} 份</Badge>
              </div>
              <ScrollArea
                className="max-h-72 rounded-md border border-slate-200"
                viewportClassName="p-2"
              >
                <div className="space-y-2">
                  {forms.map((form) => {
                    const checked = folderFormIds.includes(form.id)
                    return (
                      <button
                        key={form.id}
                        type="button"
                        onClick={() => toggleFolderForm(form.id)}
                        className={cn(
                          'flex w-full items-start gap-3 rounded-md border px-3 py-2 text-left transition-colors',
                          checked
                            ? 'border-indigo-200 bg-indigo-50'
                            : 'border-slate-200 hover:bg-slate-50',
                        )}
                      >
                        <span
                          className={cn(
                            'mt-1 size-4 rounded border',
                            checked
                              ? 'border-indigo-600 bg-indigo-600'
                              : 'border-slate-300 bg-white',
                          )}
                        />
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-slate-900">
                            {form.title}
                          </span>
                          <span className="mt-1 line-clamp-2 block text-xs leading-5 text-slate-500">
                            {form.description}
                          </span>
                        </span>
                      </button>
                    )
                  })}
                </div>
              </ScrollArea>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-4">
              {editingFolder ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                  onClick={() => setRemoveConfirmOpen(true)}
                >
                  <Trash2 className="size-4" />
                  移除資料夾
                </Button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  取消
                </Button>
                <Button type="button" onClick={saveFolder}>
                  儲存
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={removeConfirmOpen} onOpenChange={setRemoveConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>移除「{editingFolder?.name}」資料夾？</AlertDialogTitle>
            <AlertDialogDescription>
              這只會移除資料夾分類，不會刪除資料夾中的問卷。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="outline">
                取消
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button type="button" variant="destructive" onClick={removeFolder}>
                確認移除
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  )
}

function SortableFolderItem({
  folder,
  active,
  canManageForms,
  onSelect,
  onEdit,
}: {
  folder: FormFolderContract
  active: boolean
  canManageForms: boolean
  onSelect: () => void
  onEdit: () => void
}) {
  const sortable = useSortable({ id: folder.id, disabled: !canManageForms })

  return (
    <div
      ref={sortable.setNodeRef}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
      }}
      className={cn('group relative', sortable.isDragging && 'z-10 opacity-80')}
    >
      <div
        className={cn(
          'mb-1 flex min-h-10 w-full items-center gap-2 rounded-md px-2 py-1 text-sm transition-colors',
          active ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50',
        )}
      >
        {canManageForms ? (
          <button
            type="button"
            aria-label={`拖移 ${folder.name} 資料夾排序`}
            className="grid size-7 shrink-0 cursor-grab place-items-center rounded text-slate-500 hover:bg-white/80 active:cursor-grabbing"
            {...sortable.attributes}
            {...sortable.listeners}
            onClick={(event) => event.stopPropagation()}
          >
            <GripVertical className="size-4" />
          </button>
        ) : (
          <FolderOpen className="ml-1 size-4 shrink-0" />
        )}
        <button type="button" onClick={onSelect} className="min-w-0 flex-1 truncate py-1 text-left">
          {folder.name}
        </button>
        <span className={cn('text-xs', active ? 'text-indigo-700' : 'text-slate-500')}>
          {folder.formIds.length}
        </span>
      </div>
      {canManageForms && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-1 top-1 size-8 opacity-0 transition-opacity group-hover:opacity-100"
          onClick={onEdit}
          aria-label={`編輯 ${folder.name}`}
        >
          <Edit3 className="size-4" />
        </Button>
      )}
    </div>
  )
}
